/**
 * The one recipe program lint (R1a, D3). CI runs it over every recipe in the table; the runner
 * runs it on every program the server sends, before the program may claim the Mac. A recipe that
 * breaks a rule never runs, so the laws below hold for data the runner never shipped with.
 *
 * Before a yes (`steps`) the lint is an ALLOWLIST: only a vetted key, verb or menu path runs;
 * anything else waits for the person's yes (`onYes`), and a few things never run at all.
 *  1. only this version's ops (never a snapshot, a find or a click; `send` is the legacy send's);
 *  2. nothing acts after the person's typed words before a yes (no key, menu, verb or cycle); a
 *     key after a typed name only once its field was proven (a proof covers one typing and only
 *     neutral steps); a send_key only on a yes;
 *  3. keys before a yes: an EXACT member of the app's pre-yes catalog (os-recipe-catalog.ts, in the
 *     runner's own bundle), or a bare navigation key; a printable one behind the guard. A Return
 *     (any form), the table's send keys and this program's own send keys wait for the yes, but for
 *     a switcher (the catalog's exact opener and field, a typed name, settles, Return). cmd with q,
 *     w, delete, forwarddelete or escape is denied (a plain cmd+q or cmd+w only on a yes). A tab
 *     cycle presses only a vetted tab chord the app's catalog holds;
 *  4. a bare printable or delete key only right after a focus_not_editable guard;
 *  5. a link is what isUrlTemplate calls one (https, or its app's own deep-link scheme), with no
 *     person's words in its host; before a yes an https link goes only to a catalog host, never a
 *     meeting join; a call only on a yes;
 *  6. a menu names its top menu, never the Apple or Services menu; before a yes only an EXACT path
 *     of the app's catalog (a destructive one, its words anywhere in the path, never). A verb
 *     before a yes is a none-class one or a reviewed
 *     reversible one; never a script, a restart, a sleep, a lock, a Shortcut or an open;
 *  7. an irreversible recipe carries its onYes, and a first phase before a yes ends on the memo;
 *  8. an app-less recipe is a system one, which walks only verbs;
 *  9. every param and slot is declared, or made earlier in the phase (a contact, a resolve).
 * Pure, browser-safe, and it never throws: a malformed program is a violation.
 */
import { getOsVerbSpec } from './os-verbs.js';
import { LINK_ADDRESS_SCHEMES, OS_RECIPES, OS_RECIPE_SEND_DRAFT, isUrlTemplate, linkAddress, recipeTemplateSlots, type OsRecipe, type OsRecipeStep } from './os-recipes.js';
import { isRecipeStep } from './os-recipe-program.js';
import { isPreYesChord, isPreYesMenu, isPreYesSequence, isPreYesUrl, isSwitcher } from './os-recipe-catalog.js';

/** The step-op set a runner runs. A runner advertises it as OS_RUNNER_FEATURE_RECIPE_OPS. */
export const RECIPE_OPS_VERSION = 2;
/** Runner feature: this runner accepts server-sent programs of RECIPE_OPS_VERSION. */
export const OS_RUNNER_FEATURE_RECIPE_OPS = `recipe_ops:${RECIPE_OPS_VERSION}` as const;

/** Chords no first phase presses: quit, close, delete, force quit, log out. Pinned examples of the
 *  base-key rule (cmd with q, w, delete, forwarddelete or escape, whatever else is held). */
export const RECIPE_CHORD_DENYLIST: ReadonlySet<string> = new Set([
  'cmd+q', 'cmd+w', 'cmd+shift+w', 'cmd+delete', 'cmd+shift+delete', 'cmd+opt+escape', 'cmd+shift+q', 'ctrl+cmd+q',
]);

/** A menu path holding one of these (whole words, anywhere in it) is an irreversible act. */
export const DESTRUCTIVE_MENU_WORDS: readonly string[] = [
  'restart', 'shut down', 'sleep', 'lock', 'log out', 'sign out', 'revert', 'discard', 'clear', 'reset', 'eject', 'trash', 'delete', 'remove',
  'erase', 'empty', 'post', 'publish', 'share', 'call', 'pay', 'buy', 'submit', 'block', 'report', 'unsubscribe', 'send', 'archive', 'quit',
  'close', 'leave', 'end', 'bin', 'decline', 'accept', 'print', 'hang up',
];

/** The reviewed reversible 'normal' verbs a first phase may run (the research's steps): navigate,
 *  open, create, show, play, restyle the Mac. Every other non-'none' verb waits for a yes. */
export const RECIPE_STEP_VERBS: ReadonlySet<string> = new Set([
  'chrome.activate_tab', 'chrome.add_bookmark', 'chrome.go_back', 'chrome.go_forward', 'chrome.navigate', 'chrome.new_incognito_window',
  'chrome.new_window', 'chrome.open_tab', 'chrome.reload', 'chrome.stop_loading',
  'safari.activate_tab', 'safari.new_window', 'safari.open_tab', 'safari.reload', 'safari.search', 'safari.show_bookmarks',
  'finder.duplicate', 'finder.new_folder', 'finder.reveal', 'finder.set_label',
  'keynote.create_deck', 'keynote.next_slide', 'keynote.previous_slide', 'keynote.start_show', 'keynote.stop_show',
  'photos.add_selection_to_album', 'photos.create_album', 'photos.favorite_selection', 'photos.show_album', 'photos.start_slideshow', 'photos.stop_slideshow',
  'reminders.create',
  // Writes to the default calendar, which may be one others share: the asked act, kept on review.
  'calendar.create_event',
  'spotify.next', 'spotify.previous', 'spotify.play_uri',
  'sysevents.app_hide', 'sysevents.dark_mode_set', 'sysevents.window_fullscreen_set',
]);

/** The chords a tab cycle may press: next and previous tab, nothing else. */
export const CYCLE_CHORDS: readonly string[] = ['ctrl+tab', 'ctrl+shift+tab', 'cmd+shift+]', 'cmd+shift+[', 'cmd+opt+right', 'cmd+opt+left'];

const OPS: ReadonlySet<string> = new Set<OsRecipeStep['op']>([
  'activate', 'key', 'type', 'settle', 'focus_mark', 'focus_moved', 'verify_title', 'contact', 'open_url', 'await_front',
  'verb', 'pick_tab', 'memo', 'focus_not_editable', 'focus_is', 'verify_title_has', 'cycle_to_title', 'menu', 'resolve', 'send_key',
]);

/** The sidecar's chord grammar (KeyChord.swift): modifiers, then one named key, letter, digit or
 *  punctuation. No F-keys, and "+" is never a base key. */
const KEY = '(?:return|enter|tab|space|delete|backspace|forwarddelete|escape|esc|left|right|down|up|home|end|pageup|pagedown|[a-z0-9]|\\[|\\]|-|=|;|\'|,|\\.|\\/|\\\\|`)';
const CHORD = new RegExp(`^(?:(?:cmd|shift|opt|ctrl|fn)\\+)*${KEY}$`);
/** KeyChord.swift's aliases: one key, two names. */
const KEY_ALIAS: Readonly<Record<string, string>> = { backspace: 'delete', esc: 'escape', enter: 'return' };
/** Keys that type a character when pressed with no cmd, ctrl or opt. */
const PRINTABLE = /^(?:[a-z0-9]|\[|\]|-|=|;|'|,|\.|\/|\\|`|space)$/;
/** Keys that move within a view and type nothing. */
const NAV_KEYS: ReadonlySet<string> = new Set(['left', 'right', 'up', 'down', 'tab', 'escape', 'pageup', 'pagedown', 'home', 'end']);
/** Keys that, with cmd, quit, close, delete or force quit, whatever other modifiers are held. */
const CMD_DENIED_KEYS: ReadonlySet<string> = new Set(['q', 'w', 'delete', 'forwarddelete', 'escape']);
/** Keys that erase, with no cmd at all. */
const ERASE_KEYS: ReadonlySet<string> = new Set(['delete', 'forwarddelete']);

function chordParts(chord: string): { mods: string[]; key: string } {
  const parts = chord.split('+');
  const key = parts.pop()!;
  return { mods: [...new Set(parts)].sort(), key: KEY_ALIAS[key] ?? key };
}
/** One spelling per chord: modifiers sorted and deduped, the key's alias folded. */
const canonical = (chord: string): string => {
  const { mods, key } = chordParts(chord);
  return [...mods, key].join('+');
};
const CYCLE: ReadonlySet<string> = new Set(CYCLE_CHORDS.map(canonical));
/** The keys the table's own recipes send with: never pressed before a yes, in any app. */
const SEND_CHORDS: ReadonlySet<string> = new Set(OS_RECIPES.flatMap((r) => [
  ...(r.sendKey ? [r.sendKey] : []),
  ...(r.onYes ?? []).flatMap((s) => (s.op === 'send_key' ? [s.chord] : [])),
]).map(canonical));

/** A chord in a phase: fine, never pressed, or only on a yes. Before a yes, only a bare navigation
 *  key or an exact member of the app's catalog; `sends` are the keys this program sends with.
 *  'absent' = simply not in the catalog (a vetted sequence may still press it); 'yes_only' = a
 *  Return, an erase or a send key, which nothing overrides. */
function chordVerdict(chord: string, phase: 'steps' | 'onYes', app?: string, sends: ReadonlySet<string> = new Set()): 'ok' | 'denied' | 'yes_only' | 'absent' {
  if (!CHORD.test(chord)) return 'denied';
  const { mods, key } = chordParts(chord);
  if (mods.includes('cmd') && CMD_DENIED_KEYS.has(key)) {
    // On a yes, only the plain close or quit ("close this window", "quit Zoom").
    return phase === 'onYes' && mods.length === 1 && (key === 'q' || key === 'w') ? 'ok' : 'denied';
  }
  if (phase === 'onYes') return 'ok';
  const c = canonical(chord);
  if (key === 'return' || ERASE_KEYS.has(key) || SEND_CHORDS.has(c) || sends.has(c)) return 'yes_only';
  if (NAV_KEYS.has(key) && (mods.length === 0 || (key === 'tab' && mods.join('+') === 'shift'))) return 'ok';
  return app !== undefined && isPreYesChord(app, c) ? 'ok' : 'absent';
}
/** A key that types or erases in a focused text field: printable with no cmd or ctrl (opt types a
 *  character: opt+1 is ¡), or a delete without cmd. */
const needsGuard = (chord: string): boolean => {
  const { mods, key } = chordParts(chord);
  if (ERASE_KEYS.has(key)) return !mods.includes('cmd');
  return PRINTABLE.test(key) && !mods.some((m) => m === 'cmd' || m === 'ctrl');
};

/** Schemes never opened, in either phase (a script, a file, System Settings past its floor). */
const NEVER_SCHEMES: ReadonlySet<string> = new Set(['javascript', 'file', 'x-apple.systempreferences', 'data', 'vbscript']);
/** Schemes that place a call: an irreversible act, so only on a yes. */
const CALL_SCHEMES: ReadonlySet<string> = new Set(['tel', 'facetime', 'facetime-audio']);
const CONTACT_SLOTS: ReadonlySet<string> = new Set(['handle', 'phone', 'email']);

/** Steps that neither move the focus nor act: a guard and a field's proof hold across them. */
const FOCUS_NEUTRAL: ReadonlySet<string> = new Set(['settle', 'verify_title', 'verify_title_has']);

/** Invisible format characters (a zero-width joiner, a direction mark) never hide a word. */
const visible = (s: string): string => s.replace(/\p{Cf}/gu, '');
/** Lower case, ellipses and punctuation folded to spaces, padded for whole-word matching. */
const words = (s: string): string => ` ${visible(s).toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()} `;
const destructivePath = (path: readonly string[]): boolean => DESTRUCTIVE_MENU_WORDS.some((w) => words(path.join(' ')).includes(` ${w} `));
/** Menus no recipe opens, at any depth: the Apple menu (restart, log out, force quit, Settings) and
 *  Services (it runs Automator actions). */
const FORBIDDEN_MENUS: Readonly<Record<string, string>> = { apple: 'apple', '': 'apple', services: 'services' };

/** Verbs that never run in a recipe, in either phase. */
const NEVER_VERBS: ReadonlySet<string> = new Set(['shortcuts.run', 'finder.open', 'sysevents.login_item_add']);
const NEVER_VERB_ACT = /run_js|restart|shut_down|log_out|sleep|lock_screen|screensaver/;
/** Acts that wait for a yes even when the model lane calls them harmless. */
const YES_ONLY_VERB_ACT = /delete|remove|trash|empty|quit|eject|send|post|share|archive/;
function verbPolicy(id: string): 'never' | 'step' | 'yes_only' {
  const act = id.slice(id.indexOf('.') + 1);
  if (NEVER_VERBS.has(id) || NEVER_VERB_ACT.test(act)) return 'never';
  if (YES_ONLY_VERB_ACT.test(act)) return 'yes_only';
  return getOsVerbSpec(id)?.sensitivity === 'none' || RECIPE_STEP_VERBS.has(id) ? 'step' : 'yes_only';
}

const own = (o: object, k: string): boolean => Object.prototype.hasOwnProperty.call(o, k);

type Linted = Pick<OsRecipe, 'id' | 'app' | 'system' | 'params' | 'steps' | 'onYes' | 'tier' | 'sendKey'>;

/** The name a violation carries: the recipe's id, when it has one. */
const idOf = (r: unknown): string => {
  const id = (r as { id?: unknown } | null)?.id;
  return typeof id === 'string' ? id : '(no id)';
};

/** The recipe's violations, one line each ("<id>: <rule>"); empty when it may run. Never throws. */
export function lintRecipeProgram(r: Linted, opts: { schemes?: readonly string[] } = {}): string[] {
  try {
    return lint(r, opts.schemes ?? []);
  } catch {
    return [`${idOf(r)}: malformed program`];
  }
}

function lint(r: Linted, schemes: readonly string[]): string[] {
  // Legacy: app.send_draft is the one pre-onYes irreversible recipe, exempt in its shipped shape only.
  if (r.id === OS_RECIPE_SEND_DRAFT && Array.isArray(r.steps) && r.steps.length === 1 && r.steps[0]?.op === 'send' && !r.onYes?.length) return [];
  const out: string[] = [];
  const id = idOf(r);
  const say = (m: string): void => { out.push(`${id}: ${m}`); };
  if (!Array.isArray(r.steps)) say('malformed steps');
  if (r.onYes !== undefined && !Array.isArray(r.onYes)) say('malformed onYes');
  const steps: readonly OsRecipeStep[] = Array.isArray(r.steps) ? r.steps : [];
  const onYes: readonly OsRecipeStep[] = Array.isArray(r.onYes) ? r.onYes : [];

  // Rule 8: an app to act in, or a system recipe.
  if (r.app === null && r.system !== true) say('no app to act in');
  // Rule 7: an irreversible act waits for a yes, and the phase before it ends on the memo the yes re-checks.
  if (r.tier === 'irreversible' && onYes.length === 0) say('irreversible without onYes');
  if (onYes.length > 0 && steps.at(-1)?.op !== 'memo') say('onYes without a memo last in steps');
  // Rule 3 on the legacy send key (pressed only on the person's yes).
  if (r.sendKey !== undefined && (typeof r.sendKey !== 'string' || chordVerdict(r.sendKey, 'onYes') !== 'ok')) say(`chord ${String(r.sendKey)} denied`);
  // This program's own send keys never press before its yes, whatever the catalog says.
  const sends = new Set([
    ...(typeof r.sendKey === 'string' && CHORD.test(r.sendKey) ? [r.sendKey] : []),
    ...onYes.flatMap((s) => (s?.op === 'send_key' && typeof s.chord === 'string' && CHORD.test(s.chord) ? [s.chord] : [])),
  ].map(canonical));
  const app = r.app?.bundleId;

  for (const phase of ['steps', 'onYes'] as const) {
    const list = phase === 'steps' ? steps : onYes;
    const made = new Set<string>();
    let literalTyped = false;
    let unprovenSpanTyped = false;
    let fieldProven = false;
    let guarded = false;
    const param = (slot: string) => (own(r.params, slot) ? r.params[slot] : undefined);
    const known = (slot: string): boolean => param(slot) !== undefined || made.has(slot);
    const checkLink = (template: string, tableValue = false): void => {
      const lone = /^\{([a-z_]+)\}$/.exec(template);
      if (lone && !tableValue) {
        // A whole link from a param: an https address (validated at run time), or the table's own values.
        const p = param(lone[1]!);
        // A person's own https link: its host is theirs, so before a yes it waits.
        if (p?.kind === 'url') { if (phase === 'steps') say(`link ${template} only on a yes`); return; }
        if (p?.kind === 'enum' && Array.isArray(p.enum) && p.enum.length > 0) { for (const v of p.enum) checkLink(String(v), true); return; }
      }
      const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(template)?.[1]?.toLowerCase() ?? '';
      const ok = isUrlTemplate(template) && !NEVER_SCHEMES.has(scheme) && !(phase === 'steps' && CALL_SCHEMES.has(scheme)) && (scheme === 'https' || schemes.includes(scheme));
      if (!ok) { say(`scheme ${scheme || '(none)'} not allowed`); return; }
      // Before a yes an https link goes only to a vetted host, never a meeting join or the person's own.
      if (phase === 'steps' && scheme === 'https' && !isPreYesUrl(template)) say(`link ${template} only on a yes`);
      // The host is the recipe's: a slot there only for a table value, or a contact the runner
      // holds in a FaceTime or iMessage link's address.
      const at = linkAddress(template);
      const host = at ? template.slice(at.start, at.end) : '';
      for (const slot of recipeTemplateSlots(host)) {
        if (param(slot)?.kind === 'enum' || (CONTACT_SLOTS.has(slot) && made.has(slot) && LINK_ADDRESS_SCHEMES.includes(scheme))) continue;
        say(`slot ${slot} in the link's host`);
      }
    };
    const checkSlots = (template: string): void => {
      for (const slot of recipeTemplateSlots(template)) if (!known(slot)) say(`undeclared param ${slot}`);
    };
    /** Return at `i` closes a switcher: the app's exact opener and field, a typed name, settles. */
    const switcherReturn = (i: number): boolean => {
      let j = i - 1;
      while (j >= 0 && list[j]?.op === 'settle') j--;
      const typed = list[j];
      const field = list[j - 1];
      let k = j - 2;
      while (k >= 0 && list[k]?.op === 'settle') k--;
      const opener = list[k];
      if (app === undefined || typed?.op !== 'type' || param(typed.param)?.kind !== 'span' || field?.op !== 'focus_moved') return false;
      const summoned = opener?.op === 'key' ? { chord: canonical(opener.chord) } : opener?.op === 'menu' ? { menu: opener.path.join(' > ') } : undefined;
      return summoned !== undefined && isSwitcher(app, summoned, field.labelIn);
    };
    /** The key step just before `i`, and whether the guard held when it was pressed. */
    let lastKey: { chord: string; guarded: boolean } | undefined;

    list.forEach((s, i) => {
      if (!isRecipeStep(s)) { say(`malformed step ${typeof (s as { op?: unknown } | null)?.op === 'string' ? s.op : '?'}`); return; }
      // Rule 1.
      if (!OPS.has(s.op)) { say(`op ${s.op} not allowed`); return; }
      // Rule 8: a system recipe walks verbs only.
      if (r.system === true && s.op !== 'verb') say(`system recipe step ${s.op} is not a verb`);
      // Rule 9: a step's own param.
      if ('param' in s && !known(s.param)) say(`undeclared param ${s.param}`);

      // Rules 3 and 4: keys.
      if (s.op === 'cycle_to_title') {
        const verdict = chordVerdict(s.chord, phase, app, sends);
        if (verdict === 'denied') say(`chord ${s.chord} denied`);
        else if (!CYCLE.has(canonical(s.chord))) say(`cycle chord ${s.chord} not allowed`);
        else if (verdict !== 'ok') say(`cycle chord ${s.chord} only on a yes`);
      }
      const keyGuarded = guarded;
      if (s.op === 'key' || s.op === 'send_key') {
        // A send_key is an onYes op: rule 2 refuses it before a yes, whatever its chord.
        let verdict = chordVerdict(s.chord, s.op === 'send_key' ? 'onYes' : phase, app, sends);
        let guardHeld = guarded;
        if (verdict === 'yes_only' && canonical(s.chord) === 'return' && switcherReturn(i)) verdict = 'ok';
        // The second key of a vetted sequence, pressed at once, rides the guard its first key had;
        // only for a key simply absent from the catalog, never a Return, an erase or a send key.
        if (verdict === 'absent' && phase === 'steps' && lastKey && app !== undefined && isPreYesSequence(app, lastKey.chord, canonical(s.chord))) {
          verdict = 'ok';
          guardHeld = lastKey.guarded;
        }
        if (verdict === 'denied') say(`chord ${s.chord} denied`);
        else if (verdict === 'yes_only' || verdict === 'absent') say(`key ${s.chord} outside onYes`);
        else if (needsGuard(s.chord) && !guardHeld) say(`bare key ${s.chord} without focus_not_editable first`);
      }
      // Rule 6: menus and verbs.
      if (s.op === 'menu') {
        const forbidden = s.path.map((seg) => visible(seg).trim().toLowerCase()).find((seg) => own(FORBIDDEN_MENUS, seg));
        const path = s.path.join(' > ');
        if (s.path.length < 2) say(`menu ${path} needs its top menu`);
        else if (forbidden !== undefined) say(`${FORBIDDEN_MENUS[forbidden]} menu ${path} not allowed`);
        else if (phase === 'steps' && destructivePath(s.path)) say(`destructive menu ${path} outside onYes`);
        else if (phase === 'steps' && !(app !== undefined && isPreYesMenu(app, path))) say(`menu ${path} only on a yes`);
      }
      if (s.op === 'verb') {
        const policy = verbPolicy(s.id);
        if (policy === 'never') say(`verb ${s.id} never runs in a recipe`);
        else if (policy === 'yes_only' && phase === 'steps') say(`verb ${s.id} only on a yes`);
      }
      if (phase === 'steps') {
        // Rule 2: nothing acts after the person's words; a name only into a proven field.
        if (s.op === 'send_key') say('send_key outside onYes');
        if (literalTyped && (s.op === 'key' || s.op === 'cycle_to_title' || s.op === 'menu' || s.op === 'verb')) say(`${s.op} after typed literal in steps`);
        else if (unprovenSpanTyped && (s.op === 'key' || s.op === 'cycle_to_title')) say('key after typed span without a proven field');
        if (s.op === 'type') {
          if (param(s.param)?.kind === 'literal') literalTyped = true;
          else if (!fieldProven) unprovenSpanTyped = true;
        }
      }
      // Rules 5 and 9 on links and templates: a verb param is a link by the one URL rule (or its name).
      if (s.op === 'open_url') { checkLink(s.template); checkSlots(s.template); }
      if (s.op === 'verb') {
        for (const [k, t] of Object.entries(s.params)) {
          if (typeof t !== 'string') continue;
          if (k === 'url' || isUrlTemplate(t)) checkLink(t);
          checkSlots(t);
        }
      }
      // What the step makes for the steps after it.
      if (s.op === 'contact') made.add(s.want);
      if (s.op === 'resolve') made.add(s.into);
      // A guard, and a field's proof, hold only across neutral steps; a proof covers one typing.
      if (s.op === 'focus_not_editable') guarded = true;
      else if (!FOCUS_NEUTRAL.has(s.op)) guarded = false;
      if (s.op === 'focus_moved' || s.op === 'focus_is') fieldProven = true;
      else if (!FOCUS_NEUTRAL.has(s.op)) fieldProven = false;
      lastKey = s.op === 'key' && CHORD.test(s.chord) ? { chord: canonical(s.chord), guarded: keyGuarded } : undefined;
    });
  }
  return [...new Set(out)];
}
